import { useState, useRef, useEffect } from 'react';
import { Send, ChevronRight, Mic, Square, Loader2, Volume2, VolumeX, Phone } from 'lucide-react';
import { KasaraMessages } from './KasaraMessages';
import { useVoice } from './useVoice';
import { VoiceCallOverlay } from './VoiceCallOverlay';
import { useLang } from '../../i18n/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const PANEL_STYLE = { background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(24px)' };

const STRINGS = {
  en: {
    quick: ['What is DAAKYI Cloud?', 'Which regions are live today?', 'How much can I save vs AWS?', 'Help me migrate off VMware', 'How is my data secured?'],
    greeting: "Hi, I'm Kasara — your DAAKYI Cloud assistant. Ask me anything about our services, pricing, regions, migration, or security.",
    error: "I'm having a little trouble connecting right now. Please try again in a moment, or reach our team at cloud@daakyi.com.",
    busy: "You're sending messages a bit quickly — give me a few seconds and try again.",
    quickLabel: 'Quick questions:',
    placeholder: 'Ask Kasara anything...',
    role: 'DAAKYI Cloud Assistant',
    online: 'Online',
    voiceOn: 'Voice replies on',
    voiceOff: 'Voice replies off',
    micDenied: "I couldn't access your microphone — please allow mic permission in your browser and try again.",
    sttFailed: "I couldn't quite catch that — please try recording again or type your question.",
    callTitle: 'Start a voice call with Kasara'
  },
  fr: {
    quick: ["Qu'est-ce que DAAKYI Cloud ?", 'Quelles régions sont disponibles ?', 'Combien puis-je économiser vs AWS ?', 'Aidez-moi à migrer depuis VMware', 'Comment mes données sont-elles sécurisées ?'],
    greeting: "Bonjour, je suis Kasara — votre assistante DAAKYI Cloud. Posez-moi vos questions sur nos services, la tarification, les régions, la migration ou la sécurité.",
    error: "J'ai un peu de mal à me connecter pour le moment. Veuillez réessayer dans un instant, ou contactez notre équipe à cloud@daakyi.com.",
    busy: 'Vous envoyez des messages un peu vite — patientez quelques secondes et réessayez.',
    quickLabel: 'Questions rapides :',
    placeholder: 'Posez votre question à Kasara...',
    role: 'Assistante DAAKYI Cloud',
    online: 'En ligne',
    voiceOn: 'Réponses vocales activées',
    voiceOff: 'Réponses vocales désactivées',
    micDenied: "Je n'ai pas pu accéder à votre microphone — autorisez le micro dans votre navigateur puis réessayez.",
    sttFailed: "Je n'ai pas bien saisi — réessayez l'enregistrement ou écrivez votre question.",
    callTitle: 'Démarrer un appel vocal avec Kasara'
  }
};

const logDev = (...args) => {
  if (process.env.NODE_ENV !== 'production') console.warn(...args);
};

const handleSseEvent = (ev, onDelta) => {
  if (!ev.startsWith('data: ')) return;
  const payload = ev.slice(6);
  if (payload === '[DONE]') return;
  try {
    const { delta } = JSON.parse(payload);
    if (delta) onDelta(delta);
  } catch (err) {
    logDev('Skipping malformed chat chunk:', err);
  }
};

const readChatStream = async (res, onDelta) => {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop();
      events.forEach(ev => handleSseEvent(ev, onDelta));
    }
  } finally {
    reader.releaseLock();
  }
};

const getSessionId = () => {
  let id = sessionStorage.getItem('daakyi_chat_session');
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem('daakyi_chat_session', id);
  }
  return id;
};

const ChatHeader = ({ s, voiceOn, toggleVoice, isSpeaking, onCall }) => (
  <div className="bg-gradient-to-r from-[#F29B3D]/15 to-[#DD8226]/10 px-5 py-4 border-b border-[#E2E8F0]" data-no-translate>
    <div className="flex items-center gap-3">
      <div className="relative shrink-0">
        <img src="/kasara-avatar.jpg" alt="Kasara" data-testid="kasara-avatar"
          className="w-10 h-10 rounded-full object-cover border-2 border-[#F29B3D]/40" />
        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#16A34A] border-2 border-[#1A2733] shadow-[0_0_6px_rgba(22,163,74,0.6)]" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-[#1A1240] font-semibold text-sm">Kasara</h4>
        <p className="text-[#6B7280] text-[11px]">{s.role} · <span className="text-[#16A34A] font-medium">{s.online}</span></p>
      </div>
      <button
        type="button"
        onClick={onCall}
        data-testid="kasara-call-btn"
        title={s.callTitle}
        className="w-9 h-9 rounded-xl flex items-center justify-center border bg-[#16A34A]/10 border-[#16A34A]/40 text-[#16A34A] hover:bg-[#16A34A]/20 transition-all shrink-0"
      >
        <Phone className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={toggleVoice}
        data-testid="kasara-voice-toggle"
        title={voiceOn ? s.voiceOn : s.voiceOff}
        className={`w-9 h-9 rounded-xl flex items-center justify-center border transition-all shrink-0 ${
          voiceOn
            ? 'bg-[#F29B3D]/15 border-[#F29B3D]/40 text-[#A8631A]'
            : 'bg-white/60 border-[#E2E8F0] text-[#9CA3AF] hover:text-[#6B7280]'
        } ${isSpeaking ? 'animate-pulse' : ''}`}
      >
        {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

const QuickQuestions = ({ s, onAsk }) => (
  <div className="space-y-1.5 pt-2" data-no-translate>
    <p className="text-[#6B7280] text-xs mb-2">{s.quickLabel}</p>
    {s.quick.map((q) => (
      <button
        key={q}
        onClick={() => onAsk(q)}
        data-testid={`quick-q-${q.slice(0, 10).replace(/\s/g, '-').toLowerCase()}`}
        className="block w-full text-left px-3 py-2 rounded-xl text-[#374151] text-sm border border-[#E2E8F0] hover:border-[#F29B3D]/30 hover:bg-[#F29B3D]/5 hover:text-[#A8631A] transition-all flex items-center gap-2"
      >
        <ChevronRight className="w-3.5 h-3.5 text-[#A8631A] shrink-0" />
        {q}
      </button>
    ))}
  </div>
);

const MicButton = ({ recState, onStart, onStop }) => {
  if (recState === 'transcribing') {
    return (
      <span data-testid="chat-mic-transcribing" className="w-10 h-10 rounded-xl border border-[#E2E8F0] flex items-center justify-center text-[#A8631A] shrink-0">
        <Loader2 className="w-4 h-4 animate-spin" />
      </span>
    );
  }
  const recording = recState === 'recording';
  return (
    <button
      type="button"
      data-testid="chat-mic-btn"
      onClick={recording ? onStop : onStart}
      className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all shrink-0 ${
        recording
          ? 'bg-red-500 border-red-500 text-white animate-pulse'
          : 'bg-white/60 border-[#E2E8F0] text-[#6B7280] hover:border-[#F29B3D]/40 hover:text-[#A8631A]'
      }`}
    >
      {recording ? <Square className="w-3.5 h-3.5 fill-current" /> : <Mic className="w-4 h-4" />}
    </button>
  );
};

const ChatInputForm = ({ s, input, setInput, isTyping, onSubmit, recState, onMicStart, onMicStop }) => (
  <form onSubmit={onSubmit} className="px-4 py-3 border-t border-[#E2E8F0] flex items-center gap-2" data-no-translate>
    <input
      type="text"
      value={input}
      onChange={(e) => setInput(e.target.value)}
      placeholder={s.placeholder}
      maxLength={1500}
      disabled={isTyping}
      data-testid="chat-input"
      className="flex-1 bg-[#FFFFFF]/60 border border-[#E2E8F0] rounded-xl px-4 py-2.5 text-sm text-[#1A1240] placeholder-[#9CA3AF] focus:border-[#F29B3D]/40 focus:shadow-[0_0_10px_rgba(37,99,235,0.15)] outline-none transition-all disabled:opacity-60"
    />
    <MicButton recState={recState} onStart={onMicStart} onStop={onMicStop} />
    <button
      type="submit"
      data-testid="chat-send-btn"
      disabled={!input.trim() || isTyping}
      className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#F29B3D] to-[#DD8226] flex items-center justify-center text-white hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
    >
      <Send className="w-4 h-4" />
    </button>
  </form>
);

export const ChatPanel = () => {
  const { lang } = useLang();
  const s = STRINGS[lang] || STRINGS.en;
  const [messages, setMessages] = useState([{ role: 'bot', text: s.greeting }]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionIdRef = useRef(getSessionId());
  const [callOpen, setCallOpen] = useState(false);
  const { voiceOn, toggleVoice, speak, isSpeaking, stopSpeaking, recState, startRecording, stopRecording } = useVoice(lang);

  useEffect(() => {
    setMessages((prev) => (prev.length === 1 && prev[0].role === 'bot' ? [{ role: 'bot', text: s.greeting }] : prev));
  }, [s.greeting]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const appendToLastBot = (delta) => {
    setMessages(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, text: last.text + delta };
      return next;
    });
  };

  const sendMessage = async (text) => {
    const clean = text.trim();
    if (!clean || isTyping) return;
    setMessages(prev => [...prev, { role: 'user', text: clean }, { role: 'bot', text: '' }]);
    setInput('');
    setIsTyping(true);
    let reply = '';
    const onDelta = (delta) => { reply += delta; appendToLastBot(delta); };
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionIdRef.current, message: clean, lang })
      });
      if (res.status === 429) { appendToLastBot(s.busy); return; }
      if (!res.ok || !res.body) { appendToLastBot(s.error); return; }
      await readChatStream(res, onDelta);
      if (voiceOn && reply.trim()) speak(reply);
    } catch (err) {
      logDev('Chat request failed:', err);
      appendToLastBot(s.error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleMicStart = () => startRecording(
    (text) => sendMessage(text),
    (kind) => setMessages(prev => [...prev, { role: 'bot', text: kind === 'mic' ? s.micDenied : s.sttFailed }])
  );

  return (
    <div
      data-testid="chat-widget-panel"
      className="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-32px)] sm:w-[380px] max-w-[380px] rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-[0_0_60px_rgba(37,99,235,0.15)]"
      style={PANEL_STYLE}
    >
      <ChatHeader s={s} voiceOn={voiceOn} toggleVoice={toggleVoice} isSpeaking={isSpeaking}
        onCall={() => { stopSpeaking(); setCallOpen(true); }} />

      <div className="h-[340px] overflow-y-auto px-4 py-4 space-y-3 custom-scrollbar" data-no-translate>
        <KasaraMessages messages={messages} isTyping={isTyping} />
        {messages.length <= 1 && <QuickQuestions s={s} onAsk={sendMessage} />}
        <div ref={messagesEndRef} />
      </div>

      <ChatInputForm
        s={s} input={input} setInput={setInput} isTyping={isTyping} onSubmit={handleSubmit}
        recState={recState} onMicStart={handleMicStart} onMicStop={stopRecording}
      />

      {callOpen && (
        <VoiceCallOverlay
          lang={lang}
          sessionId={sessionIdRef.current}
          onExchange={(userText, botText) => setMessages(prev => [...prev, { role: 'user', text: userText }, { role: 'bot', text: botText }])}
          onClose={() => setCallOpen(false)}
        />
      )}
    </div>
  );
};
